/* @jsxImportSource react */
'use client';

import React from 'react';

import { cn } from '../../utils/cn';
import { Container, type ContainerVariantProps } from '../../utils/container';

/**
 * Grid component props. Accepts all standard `<div>` attributes plus an
 * optional `as` prop to render as a semantic element (e.g. `section`, `nav`).
 * Passing `onClick` renders the Grid as a `<button>` (requires `aria-label`);
 * passing `href` renders the Grid as an `<a>` (requires `aria-label`).
 *
 * @example
 * ```tsx
 * <Grid className="grid-cols-3 gap-4">…</Grid>
 * <Grid as="section" className="grid-cols-3 gap-4">…</Grid>
 * <Grid onClick={open} aria-label="Open details">…</Grid>
 * <Grid href="/foo" aria-label="Go to foo">…</Grid>
 * ```
 */
export type GridProps = ContainerVariantProps;

/**
 * CSS grid container. Applies `display: grid` by default; pass `className`
 * to set columns, rows, gap, and any other grid utilities. Conflicting user
 * classes win via `tailwind-merge`.
 *
 * @example
 * ```tsx
 * <Grid className="grid-cols-3 gap-4">…</Grid>
 * <Grid className="grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">…</Grid>
 * ```
 */
export const Grid = React.forwardRef<HTMLElement, GridProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Container ref={ref} className={cn('grid', className)} {...props}>
        {children}
      </Container>
    );
  },
);

Grid.displayName = 'Grid';
